import { createLazyResource, toast, useLocalization, createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { hasActivePermission } from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { parseErrorMessage } from '~/errors/errorCodeMap';
import { companyBusinessInfoRepository } from '~/repositories/businessInfoRepository';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { BusinessInfoPhase, BusinessInfoRevisionKind, BusinessInfoRevisionStatus } from '~/swagger/companies/Api';
import { COMPANY_BUSINESS_INFO_PATCH_DEFAULTS } from '~/utils/companyBusinessInfoDefaults';
import { getBusinessInfoAbandonToastKey } from '~/utils/companyBusinessInfoLabels';
import {
  buildPatchDraftPayloadIfChanged,
  buildPrincipalClientKeyToIdMap,
  buildPrincipalIndexToIdMap,
  buildPrincipalMutations,
  collectDocumentActions,
  collectDocumentDeleteActions,
} from '~/utils/companyBusinessInfoPayload';
import { normalizeCompanyBusinessInfoPayload } from '~/utils/companyBusinessInfoValidation';
import type { BusinessInfoDraftDto, BusinessInfoItemDto, InitBusinessInfoDraftRequestDto } from '~/swagger/companies/Api';
import type { CompanyBusinessInfoFormPayload } from '~/utils/companyBusinessInfoPayload';

type SaveDraftResult = {
  draft: BusinessInfoDraftDto;
  fileOperationsFailed: boolean;
};

type SaveDraftOptions = {
  silent?: boolean;
};

const SUBMIT_ERROR_TOAST_OPTIONS = {
  timeout: 8000,
  showCloseButton: true,
} as const;

const LIST_PHASES = [BusinessInfoPhase.Staged, BusinessInfoPhase.Materialized];

export const [CompanyBusinessInfoProvider, useCompanyBusinessInfo] = createMagicDoorContext('CompanyBusinessInfo', () => {
  const { t } = useLocalization();
  const [activeDraft, setActiveDraft] = createSignal<BusinessInfoDraftDto | null>(null);

  const [companyBusinessInfoList, companyBusinessInfoListActions] = createLazyResource(async () => {
    if (!hasActivePermission('business_info', 'read')) return [];
    try {
      return await companyBusinessInfoRepository.getBusinessInfoList(LIST_PHASES);
    } catch {
      return [];
    }
  });

  const materializedBusinessInfoList = createMemo(() =>
    (companyBusinessInfoList() ?? []).filter((item) => item.phase === BusinessInfoPhase.Materialized)
  );

  const isShowBusinessInfoBanner = createMemo(() => materializedBusinessInfoList().length === 0);

  const initDraft = async (data: InitBusinessInfoDraftRequestDto) => {
    const draft = await companyBusinessInfoRepository.initDraft({ ...data, legalName: data.legalName.trim() });
    setActiveDraft(draft);
    await companyBusinessInfoListActions.refetch();
    return draft;
  };

  const openDraftEditor = async (item: BusinessInfoItemDto): Promise<BusinessInfoDraftDto> => {
    const { id, phase, status } = item;

    if (phase === BusinessInfoPhase.Materialized && !status) {
      const draft = await companyBusinessInfoRepository.startUpdateDraft(id);
      setActiveDraft(draft);
      return draft;
    }

    const draft = await companyBusinessInfoRepository.getDraft(id);
    setActiveDraft(draft);
    return draft;
  };

  const loadDraft = createMutation(async (businessInfoId: string) => {
    const draft = await companyBusinessInfoRepository.getDraft(businessInfoId);
    setActiveDraft(draft);
    return draft;
  });

  const saveDraft = async (
    businessInfoId: string,
    payload: CompanyBusinessInfoFormPayload,
    options?: SaveDraftOptions
  ): Promise<SaveDraftResult> => {
    const showSaveSuccessToast = !options?.silent;
    const cachedDraft = activeDraft();
    let draft = cachedDraft?.businessInfoId === businessInfoId ? cachedDraft : await companyBusinessInfoRepository.getDraft(businessInfoId);
    const normalizedPayload = normalizeCompanyBusinessInfoPayload(payload);

    const patchPayload = buildPatchDraftPayloadIfChanged(draft, normalizedPayload, COMPANY_BUSINESS_INFO_PATCH_DEFAULTS);
    const principalMutations = buildPrincipalMutations(normalizedPayload.principals, draft.principals);
    const deleteActions = collectDocumentDeleteActions(normalizedPayload);
    const uploadActions = collectDocumentActions(normalizedPayload);

    if (!patchPayload && principalMutations.length === 0 && deleteActions.length === 0 && uploadActions.length === 0) {
      if (showSaveSuccessToast) {
        toast.success(t('Business information saved successfully'));
      }
      return { draft, fileOperationsFailed: false };
    }

    if (patchPayload) {
      draft = await companyBusinessInfoRepository.patchDraft(businessInfoId, patchPayload);
    }

    const principalClientKeyToIdMap: Record<string, string> = {};
    normalizedPayload.principals?.forEach((principal) => {
      if (principal.clientKey && principal.id) {
        principalClientKeyToIdMap[principal.clientKey] = principal.id;
      }
    });

    for (const mutation of principalMutations) {
      if (mutation.type === 'add' && mutation.payload) {
        const existingPrincipalIds = new Set(draft.principals.map((principal) => principal.id).filter(Boolean));
        draft = await companyBusinessInfoRepository.addPrincipal(businessInfoId, mutation.payload);
        const createdPrincipal = draft.principals.find((principal) => principal.id && !existingPrincipalIds.has(principal.id));
        if (mutation.clientKey && createdPrincipal?.id) {
          principalClientKeyToIdMap[mutation.clientKey] = createdPrincipal.id;
        }
      } else if (mutation.type === 'patch' && mutation.principalId && mutation.payload) {
        draft = await companyBusinessInfoRepository.patchPrincipal(businessInfoId, mutation.principalId, mutation.payload);
      } else if (mutation.type === 'delete' && mutation.principalId) {
        draft = await companyBusinessInfoRepository.deletePrincipal(businessInfoId, mutation.principalId);
      }
    }

    const principalIdMap = buildPrincipalIndexToIdMap(normalizedPayload.principals, draft.principals);
    Object.assign(principalClientKeyToIdMap, buildPrincipalClientKeyToIdMap(normalizedPayload.principals, draft.principals));

    const deleteResults = await Promise.allSettled(
      deleteActions.map((action) => {
        const documentId =
          draft.documents.find((d) => d.id === action.documentId || d.fileId === action.documentId)?.id ?? action.documentId;
        return companyBusinessInfoRepository.deleteDocument(businessInfoId, documentId);
      })
    );

    const uploadResults = await Promise.allSettled(
      uploadActions.map(async (action) => {
        const principalId = action.principalClientKey
          ? principalClientKeyToIdMap[action.principalClientKey]
          : action.principalIndex !== undefined
            ? principalIdMap[action.principalIndex]
            : undefined;
        if (action.principalIndex !== undefined && !principalId) {
          throw new Error(`Missing principal id for obligation ${action.obligationKey} at index ${action.principalIndex}`);
        }

        const sessionId = await uploadSessionRepository.uploadFile(
          companyBusinessInfoRepository.getUploadSessionUrl(businessInfoId),
          action.rawFile
        );
        return companyBusinessInfoRepository.attachDocument(businessInfoId, {
          uploadSessionId: sessionId,
          obligationKey: action.obligationKey,
          principalId,
        });
      })
    );

    const deleteFailures = deleteResults.filter((r) => r.status === 'rejected').length;
    const uploadFailures = uploadResults.filter((r) => r.status === 'rejected').length;
    const totalFailures = deleteFailures + uploadFailures;

    draft = await companyBusinessInfoRepository.getDraft(businessInfoId);

    setActiveDraft(draft);
    await companyBusinessInfoListActions.refetch();

    if (totalFailures > 0) {
      toast.warning(t('{count} file operations failed', { count: totalFailures }));
      return { draft, fileOperationsFailed: true };
    }

    if (showSaveSuccessToast) {
      toast.success(t('Business information saved successfully'));
    }
    return { draft, fileOperationsFailed: false };
  };

  const submitDraft = async (businessInfoId: string) => {
    try {
      const draft = await companyBusinessInfoRepository.submitDraft(businessInfoId, false);
      setActiveDraft(draft);
      await companyBusinessInfoListActions.refetch();
      return draft;
    } catch (error) {
      toast.error(parseErrorMessage(error instanceof Error ? error : undefined), SUBMIT_ERROR_TOAST_OPTIONS);
      throw error;
    }
  };

  const withdrawDraft = async (businessInfoId: string, reason?: string) => {
    const draft = await companyBusinessInfoRepository.withdrawDraft(businessInfoId, reason ? { reason } : undefined);
    setActiveDraft(draft);
    await companyBusinessInfoListActions.refetch();
    toast.success(t('Submission withdrawn'));
    return draft;
  };

  const abandonDraft = async (businessInfoId: string, reason?: string, type?: BusinessInfoRevisionKind) => {
    await companyBusinessInfoRepository.abandonDraft(businessInfoId, reason ? { reason } : undefined);
    setActiveDraft(null);
    await companyBusinessInfoListActions.refetch();
    toast.success(t(getBusinessInfoAbandonToastKey(type)));
  };

  const setPrimary = async (businessInfoId: string) => {
    await companyBusinessInfoRepository.setPrimary(businessInfoId);
    await companyBusinessInfoListActions.refetch();
    toast.success(t('Primary business information updated'));
  };

  const findStagedInitialDraftItem = createMemo(() => {
    const items = companyBusinessInfoList() ?? [];
    return items.find(
      (item) =>
        item.phase === BusinessInfoPhase.Staged &&
        item.type === BusinessInfoRevisionKind.Initial &&
        item.status === BusinessInfoRevisionStatus.Draft
    );
  });

  return {
    companyBusinessInfoList,
    companyBusinessInfoListActions,
    isShowBusinessInfoBanner,
    activeDraft,
    setActiveDraft,
    initDraft,
    openDraftEditor,
    loadDraft,
    saveDraft,
    submitDraft,
    withdrawDraft,
    abandonDraft,
    setPrimary,
    findStagedInitialDraftItem,
  };
});
